"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { DiamondUnlockAction, useDiamondUpgrade, type DiamondDestination } from "@/components/billing/diamond-upgrade";
import { ArrowUpRight, Bell, BellRing, Lock, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { usePushSubscription } from "@/hooks/use-push-subscription";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { PlayerAvatar } from "@/components/ui/avatar";
import { SheetShell, useDismiss } from "@/components/odds-screen/live/top-menus";
import { dayClock } from "@/components/odds-screen/live/time";
import { useNowTick } from "@/hooks/use-now-tick";
import type { Race, RaceLeg } from "@/lib/odds-screen/live/types";
import { RACE_APP_STORE_PAGE, RACE_APP_WAIT_MS, RACE_ASSUMED_NOTE, raceAssumedLabel, raceAssumedText, raceEvText, raceLegLine, raceMatchup, racePctText, racePlaceLink, raceSinceText } from "@/lib/fantasy-props/race";
import { phCapture, trackBetLinkClick } from "@/utils/analytics";
import { formatMultiple } from "./model";
import { quietClass } from "./chrome";
import { valuePillClass } from "./board-parts";

export type RaceSlot = {
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onClose: () => void;
  readonly state:
    | { readonly kind: "locked" }
    | { readonly kind: "idle"; readonly closed: boolean }
    | { readonly kind: "live"; readonly race: Race; readonly closed: boolean };
};

type RaceHandlers = Omit<RaceSlot, "state"> & { readonly isMobile: boolean };

const RACE_CHIP = "bg-[#c45cff]/[0.16] text-[#c45cff]";
const RACE_DOT = "bg-[#c45cff]";
const MUTED_CHIP = "bg-white/[0.06] text-zinc-400";
const EST_MARK = "mr-1 text-[10px] font-medium text-zinc-500";
const LEAGUE_TAG = "mr-1.5 inline-block rounded bg-white/[0.06] px-1 align-[1px] text-[10px] font-semibold leading-[14px] tracking-[0.04em] text-zinc-400";

function evChipClass(ev: number | null): string {
  return ev !== null && ev > 0 ? "bg-accent-value/15 text-accent-value" : MUTED_CHIP;
}

export function RaceBadge({ boost, onOpen }: { boost: number; onOpen: () => void }) {
  return (
    <button
      type="button"
      aria-label={`In the Race to Place, ${formatMultiple(boost)}`}
      onClick={(event) => { event.stopPropagation(); onOpen(); }}
      className={cn("data-num inline-flex h-4 flex-shrink-0 items-center rounded px-1 text-[10px] font-semibold leading-4 tracking-[0.04em] hover:bg-[#c45cff]/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", RACE_CHIP)}
    >
      RTP {formatMultiple(boost)}
    </button>
  );
}

function RacePill({ race, open, tall, onClick }: { race: Race; open: boolean; tall: boolean; onClick: () => void }) {
  const ev = race.ev === null ? "" : `, EV ${raceEvText(race.ev)}`;
  const estimate = race.assumed > 0 ? `, ${raceAssumedLabel(race.assumed)}` : "";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`Race to Place, ${formatMultiple(race.boost)}, ${race.taken} of ${race.seats} seats${ev}${estimate}`}
      title={race.assumed > 0 ? RACE_ASSUMED_NOTE : undefined}
      className={quietClass(open, tall)}
    >
      <SportsbookLogoMark sportsbook={race.book} size={16} decorative />
      <span>RTP</span>
      <span className={cn("data-num rounded px-1 text-[11px] font-semibold leading-[18px]", RACE_CHIP)}>{formatMultiple(race.boost)}</span>
      {race.ev !== null && (
        <span className={cn("data-num rounded px-1 text-[11px] font-semibold leading-[18px]", evChipClass(race.ev))}>
          {race.assumed > 0 && <span className={EST_MARK}>est.</span>}
          {raceEvText(race.ev)}
        </span>
      )}
      <span className="data-num text-[11px] text-zinc-500">{race.taken}/{race.seats}</span>
    </button>
  );
}

function RaceLegRow({ leg }: { leg: RaceLeg }) {
  const tag = [leg.pos, leg.team].filter((part) => part !== "").join(" · ");
  return (
    <li className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 border-b border-white/[0.06] py-2.5 last:border-b-0">
      <PlayerAvatar name={leg.player} imageUrl={leg.img} size={32} />
      <div className="flex min-w-0 flex-col">
        <span className="text-[11px] leading-4 text-zinc-500">
          {leg.league !== "" && <span className={LEAGUE_TAG}>{leg.league.toUpperCase()}</span>}
          {raceMatchup(leg)}
          {leg.startMs > 0 && <span className="whitespace-nowrap"> · {dayClock(leg.startMs)}</span>}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="truncate text-[13px] font-semibold leading-[18px] text-foreground">{leg.player}</span>
          {tag !== "" && <span className="truncate text-[11px] font-medium leading-[18px] text-zinc-500">{tag}</span>}
        </span>
        <span className="text-[12px] leading-4 text-zinc-400">{raceLegLine(leg)}</span>
      </div>
      <div className="flex flex-col items-end" title={leg.assumed ? RACE_ASSUMED_NOTE : undefined}>
        <span className="data-num text-[13px] font-semibold leading-4 text-foreground">
          {leg.assumed && <span className={EST_MARK}>est.</span>}
          {racePctText(leg.fair)}
        </span>
        <span className={cn("data-num whitespace-nowrap text-[10px] leading-[14px]", leg.ev !== null && leg.ev > 0 ? "text-accent-value" : "text-zinc-500")}>
          {leg.ev === null ? "\u00a0" : <>{leg.assumed && <span className={EST_MARK}>est.</span>}{`EV ${raceEvText(leg.ev)}`}</>}
        </span>
      </div>
    </li>
  );
}

function PanelClose({ onClose }: { onClose: () => void }) {
  return (
    <button type="button" aria-label="Close" onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
      <X size={14} aria-hidden="true" />
    </button>
  );
}

function panelClass(framed: boolean): string {
  return cn("flex flex-col gap-3 px-4 pt-3.5", framed ? "pb-3.5" : "pb-[max(0.875rem,env(safe-area-inset-bottom))]");
}

function RaceHeader({ framed, onClose, children }: { framed: boolean; onClose: () => void; children?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5">
      <SportsbookLogoMark sportsbook="betr" size={22} />
      {framed && <span className="text-[13px] font-semibold text-foreground">Race to Place</span>}
      {children}
      <span className="flex-grow" />
      {framed && <PanelClose onClose={onClose} />}
    </div>
  );
}

const PLACE_BUTTON = "tap flex items-center justify-center gap-2 rounded-md border border-surface-line bg-white/[0.04] px-4 text-[13px] font-semibold text-foreground transition-colors hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

function fallBackToStorePage() {
  const timer = window.setTimeout(() => {
    if (document.visibilityState === "visible") window.open(RACE_APP_STORE_PAGE, "_self");
  }, RACE_APP_WAIT_MS);
  document.addEventListener("visibilitychange", () => window.clearTimeout(timer), { once: true });
}

function placeButtonClass(tall: boolean): string {
  return cn(PLACE_BUTTON, tall ? "h-11" : "h-9");
}

function RacePlaceButton({ race, tall }: { race: Race; tall: boolean }) {
  const link = racePlaceLink(typeof navigator === "undefined" ? "" : navigator.userAgent);
  const onClick = () => {
    trackBetLinkClick({ sportsbook: race.book, page: "optimizer", bet_type: "prop", ev_percentage: race.ev === null ? undefined : race.ev * 100, click_source: "cta" });
    if (link.app) fallBackToStorePage();
  };
  return (
    <a href={link.href} onClick={onClick} className={placeButtonClass(tall)} {...(link.app ? {} : { target: "_blank", rel: "noopener noreferrer" })}>
      <SportsbookLogoMark sportsbook={race.book} size={18} decorative />
      Open Betr
      <ArrowUpRight size={12} strokeWidth={2.5} aria-hidden="true" className="text-zinc-400" />
    </a>
  );
}

function RacePanel({ race, closed, onClose, framed }: { race: Race; closed: boolean; onClose: () => void; framed: boolean }) {
  const nowMs = useNowTick(true, 15_000);
  const seats = Array.from({ length: race.seats }, (_, index) => index < race.taken);
  return (
    <div className={panelClass(framed)}>
      <RaceHeader framed={framed} onClose={onClose}>
        <span className={cn("data-num inline-flex h-[22px] items-center gap-1.5 rounded-md px-[7px] text-[11px] font-semibold", RACE_CHIP)}>
          <span className={cn("h-1.5 w-1.5 rounded-full", RACE_DOT)} aria-hidden="true" />
          {raceSinceText(race.sinceMs, nowMs)}
        </span>
      </RaceHeader>
      {closed && <p className="text-[13px] text-zinc-400">The race from your alert has closed. Here&apos;s the newest one.</p>}
      <div className="flex items-center gap-3.5">
        <span className="flex items-baseline gap-1.5">
          <s className="data-num text-[12px] text-zinc-500">{formatMultiple(race.mult)}</s>
          <span className="data-num text-[22px] font-bold leading-[26px] text-foreground">{formatMultiple(race.boost)}</span>
        </span>
        <span className="h-[22px] w-px bg-white/10" aria-hidden="true" />
        <span className="flex items-center gap-2.5">
          <span aria-label={`Seats taken, ${race.taken} of ${race.seats}`} className="grid h-2 gap-[3px]" style={{ gridTemplateColumns: `repeat(${race.seats}, 7px)` }}>
            {seats.map((taken, index) => <span key={index} className={cn("rounded-[2px]", taken ? RACE_DOT : "bg-white/[0.14]")} />)}
          </span>
          <span className="data-num text-[12px] font-semibold text-foreground">{race.taken} of {race.seats} seats</span>
        </span>
      </div>
      <ul className="flex flex-col">
        {race.legs.map((leg, index) => <RaceLegRow key={`${leg.row ?? leg.player}:${index}`} leg={leg} />)}
      </ul>
      <div className="flex flex-col gap-1.5 border-t border-white/[0.06] pt-3">
        <div className="flex items-center justify-between gap-3">
          <span className="flex flex-col">
            <span className="whitespace-nowrap text-[12px] text-zinc-400">
              <span className="data-num text-[15px] font-semibold text-foreground">{racePctText(race.hit)}</span> to hit
            </span>
            <span className="data-num whitespace-nowrap text-[11px] text-zinc-500">need {racePctText(race.need)}</span>
          </span>
          <span className={cn(valuePillClass(race.ev !== null && race.ev > 0), "flex-shrink-0")}>{raceEvText(race.ev)} EV</span>
        </div>
        {race.assumed > 0 && <span className="text-[11px] text-zinc-500">{raceAssumedText(race.assumed)} · {RACE_ASSUMED_NOTE}</span>}
      </div>
      <div className="flex flex-col gap-1.5">
        <RacePlaceButton race={race} tall={!framed} />
        <span className="data-num text-center text-[11px] text-zinc-500">${race.min} to ${race.max} entry · profit in Playthrough Cash</span>
      </div>
      <RaceAlertsRow tall={!framed} />
    </div>
  );
}

const ALERTS_HREF = "/account/notifications";

function RaceAlertsRow({ tall }: { tall: boolean }) {
  const { user } = useAuth();
  const push = usePushSubscription();
  const [pending, setPending] = useState<"prompt" | "quiet" | null>(null);
  const hidden = !user || (push.busy && pending === null) || push.support === null || push.support === "unsupported" || push.loadFailed || push.optedIn === null;
  const on = push.enabledOnThisDevice && push.optedIn?.has("race_plus_ev") === true;
  const prompt = hidden || (push.support === "ready" && on) ? null : push.support;
  const shownRef = useRef(false);
  useEffect(() => {
    if (prompt === null || shownRef.current) return;
    shownRef.current = true;
    phCapture("push_prompt_shown", { source: "panel", support: prompt });
  }, [prompt]);
  if (hidden) return null;
  const control = cn(quietClass(false, tall), "text-[12px]", tall && "h-11");
  const clicked = () => phCapture("push_prompt_clicked", { source: "panel", support: push.support });
  const turnOn = () => {
    setPending(Notification.permission === "default" ? "prompt" : "quiet");
    clicked();
    void push.enable("panel", "race_plus_ev").finally(() => setPending(null));
  };
  const row = (text: React.ReactNode, action: React.ReactNode) => (
    <div className="flex min-h-9 items-center justify-between gap-3 border-t border-white/[0.06] pt-2.5">
      <span className="flex items-center gap-1.5 text-[12px] text-zinc-500">{text}</span>
      {action}
    </div>
  );
  if (push.support === "needs_install") return row("Get race alerts on iPhone.", <Link href={ALERTS_HREF} onClick={clicked} className={control}>See how</Link>);
  if (push.support === "denied") return row("Alerts are blocked in this browser.", <Link href={ALERTS_HREF} className={control}>Fix</Link>);
  if (on) return row(<><BellRing size={12} aria-hidden="true" />Alerts on for this device</>, <Link href={ALERTS_HREF} className={control}>Manage</Link>);
  return row(
    pending === "prompt" ? "Click Allow in your browser's prompt." : "Get a push when a new race prices +EV.",
    <button type="button" disabled={pending !== null} onClick={turnOn} className={cn(control, "disabled:opacity-50")}><Bell size={12} aria-hidden="true" />Turn on alerts</button>,
  );
}

function RaceLockPill({ open, tall, onClick }: { open: boolean; tall: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label="Race to Place, Diamond only"
      title="Race to Place is a Diamond tool"
      className={quietClass(open, tall)}
    >
      <SportsbookLogoMark sportsbook="betr" size={16} decorative />
      <span>RTP</span>
      <span aria-hidden="true" className={cn("data-num select-none rounded px-1 text-[11px] font-semibold leading-[18px] blur-[3px]", RACE_CHIP)}>0.00x</span>
      <span aria-hidden="true" className={cn("data-num select-none rounded px-1 text-[11px] font-semibold leading-[18px] blur-[3px]", MUTED_CHIP)}>0.0%</span>
      <Lock size={12} aria-hidden="true" className="text-zinc-500" />
    </button>
  );
}

function RaceLockPanel({ framed, onClose, action }: { framed: boolean; onClose: () => void; action: React.ReactNode }) {
  return (
    <div className={panelClass(framed)}>
      <div className="flex items-center gap-2.5">
        <SportsbookLogoMark sportsbook="betr" size={22} />
        <span className="text-[15px] font-semibold text-foreground">Race to Place is a Diamond tool</span>
        <span className="flex-grow" />
        {framed && <PanelClose onClose={onClose} />}
      </div>
      <p className="text-[13px] text-zinc-400">Betr posts a boosted-multiplier slip with limited seats. Diamond prices every leg and shows the slip&apos;s hit rate and EV.</p>
      {action}
    </div>
  );
}

function RaceLock({ open, onToggle, onClose, isMobile }: RaceHandlers) {
  const upgrade = useDiamondUpgrade("race_to_place_gate", open);
  const toggle = () => {
    if (!open) phCapture("race_gate_shown", { trigger: "pill" });
    onToggle();
  };
  const choose = (destination: DiamondDestination) => {
    phCapture("race_gate_clicked", { destination });
    if (destination === "upgrade_modal") onClose();
  };
  return (
    <>
      <RaceFrame open={open} isMobile={isMobile} onClose={onClose} trigger={<RaceLockPill open={open} tall={isMobile} onClick={toggle} />}>
        <RaceLockPanel framed={!isMobile} onClose={onClose} action={<DiamondUnlockAction upgrade={upgrade} className={cn(placeButtonClass(isMobile), "text-primary")} onChoose={choose}>Unlock with Diamond</DiamondUnlockAction>} />
      </RaceFrame>
      {upgrade.modal}
    </>
  );
}

function RaceFrame({ open, isMobile, onClose, trigger, children }: { open: boolean; isMobile: boolean; onClose: () => void; trigger: React.ReactNode; children: React.ReactNode }) {
  const ref = useDismiss(open && !isMobile, onClose);
  if (isMobile) {
    return (
      <>
        {trigger}
        <SheetShell open={open} title="Race to Place" onClose={onClose}>{children}</SheetShell>
      </>
    );
  }
  return (
    <div ref={ref} className="relative flex-shrink-0">
      {trigger}
      {open && (
        <div
          role="dialog"
          aria-label="Race to Place"
          className="absolute right-0 top-[calc(100%+6px)] z-30 w-[440px] origin-top-right rounded-lg border border-surface-line bg-surface-overlay shadow-xl animate-in fade-in-0 zoom-in-95 duration-150 motion-reduce:animate-none"
        >
          {children}
        </div>
      )}
    </div>
  );
}

function RaceIdlePill({ open, tall, onClick }: { open: boolean; tall: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label="Race to Place, no race posted right now"
      className={quietClass(open, tall)}
    >
      <SportsbookLogoMark sportsbook="betr" size={16} decorative />
      <span>RTP</span>
      <span className={cn("rounded px-1 text-[11px] font-semibold leading-[18px]", MUTED_CHIP)}>No race</span>
    </button>
  );
}

function RaceIdlePanel({ closed, onClose, framed }: { closed: boolean; onClose: () => void; framed: boolean }) {
  return (
    <div className={panelClass(framed)}>
      {framed && <RaceHeader framed onClose={onClose} />}
      <p className="text-[13px] text-zinc-400">
        {closed && "That race has closed. "}Betr has no race posted right now. A new one usually lands within minutes of the last one closing.
      </p>
      <RaceAlertsRow tall={!framed} />
    </div>
  );
}

export function RaceControl({ state, open, onToggle, onClose, isMobile }: RaceSlot & { isMobile: boolean }): React.ReactElement {
  switch (state.kind) {
    case "locked":
      return <RaceLock open={open} onToggle={onToggle} onClose={onClose} isMobile={isMobile} />;
    case "idle":
      return (
        <RaceFrame open={open} isMobile={isMobile} onClose={onClose} trigger={<RaceIdlePill open={open} tall={isMobile} onClick={onToggle} />}>
          <RaceIdlePanel closed={state.closed} onClose={onClose} framed={!isMobile} />
        </RaceFrame>
      );
    case "live":
      return (
        <RaceFrame open={open} isMobile={isMobile} onClose={onClose} trigger={<RacePill race={state.race} open={open} tall={isMobile} onClick={onToggle} />}>
          <RacePanel race={state.race} closed={state.closed} onClose={onClose} framed={!isMobile} />
        </RaceFrame>
      );
  }
}
